import React, { useEffect, useRef, useState } from "react";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Col, Row } from "react-bootstrap";
import { List, Spin, Tooltip } from "antd";
import { getClientList } from "../../redux/Slices/clientSidebarSlice";
import { Avatar } from "@mui/material";
import ToastNotification from "../../components/Common/ToastNotification";
import AreaChartWithFilters from "./AreaChartWithFilters";
import EditWeightGoalModal from "./EditWeightGoalModal";
import MembershipOverview from "./MembershipOverview";
import ClientCheckInsDetails from "./ClientCheckInsDetails";
import useFetchCoachProfile from "../../utils/useFetchCoachProfile";
import Reminder from "./Reminder";
import ClientWorkoutTab from "./ClientWorkoutTab";
import { getClientInformation } from "../../redux/Slices/clientInformationSlice";
import { CONSTANTS } from "../../utils/Constant/Constant";
import ChatPopup from "./ChatPopup";
import appInsights from "../../AppInsights";
import ChatbotContainer from "../Chatbot/ChatbotContainer";

const ClientSideBar = () => {
  const { trans } = useCustomTranslation();
  useFetchCoachProfile();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profileData } = useSelector((state) => state.profile);
  const {
    firstName,
    lastName,
    goal,
    status,
    clientMeasurements,
    leadId,
    loading: membershipOverviewLoading,
  } = useSelector((state) => state.membershipOverview);

  const {
    clientList,
    totalCount,
    loading: apiLoading,
    error,
  } = useSelector((state) => state.clientList);

  const containerRef = useRef(null);
  const { state: clientDetails } = useLocation();
  const { client, clientDetail, type, isConversationEnabled, appliedFilter } =
    clientDetails || {};

  // eslint-disable-next-line no-unused-vars
  const [scrollHeight, setScrollHeight] = useState(0);

  const [selectedClientId, setSelectedClientId] = useState(client);
  const [selectedClient, setSelectedClient] = useState(clientDetail);
  const [toastConfig, setToastConfig] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [notificationType, setNotificationType] = useState(null);
  // set Chat Popup state
  const [isChatPopupOpen, setIsChatPopupOpen] = useState(
    isConversationEnabled || false
  );

  const coachId = profileData?.coachId;

  // 🔹 AppInsights – Page View
  useEffect(() => {
    appInsights.trackPageView({
      name: "ClientSideBar",
      properties: {
        coachId,
      },
    });
  }, [coachId]);

  useEffect(() => {
    const updateScrollHeight = () => {
      const windowHeight = window.innerHeight;
      const headerHeight =
        document.querySelector(".createClientHeading")?.offsetHeight || 0;
      const marginTop = 160;
      setScrollHeight(windowHeight - headerHeight - marginTop);
    };

    updateScrollHeight();
    window.addEventListener("resize", updateScrollHeight);

    return () => {
      window.removeEventListener("resize", updateScrollHeight);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedClientId, client, type]);

  useEffect(() => {
    if (client && type) {
      type && setNotificationType(type);
      client && setSelectedClientId(client);
    }
    if (isConversationEnabled !== undefined) {
      setIsChatPopupOpen(isConversationEnabled);
    }
  }, [clientDetails, client, type, isConversationEnabled]);

  // NEW: re-sync isChatPopupOpen once selectedClient is populated from list
  useEffect(() => {
    if (selectedClient?.conversationId) {
      setIsChatPopupOpen(true);
    }
  }, [selectedClient?.conversationId]);

  useEffect(() => {
    if (page === 1) {
      setLoading(true);
    }
    if (coachId) {
      dispatch(
        getClientList({
          coachId: coachId,
          page,
          pageSize: 10,
          appliedFilter,
        })
      ).finally(() => setLoading(false));
    }
  }, [page, dispatch, coachId, appliedFilter]);

  useEffect(() => {
    if (clientList.length > 0 && !selectedClientId) {
      setSelectedClientId(clientList[0].id);
      setSelectedClient(clientList[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientList, clientDetails]);

  useEffect(() => {
    if (client && clientList && clientList.length > 0) {
      const matchedClient = clientList.find(
        (clientItem) => clientItem.id === client
      );

      if (matchedClient) {
        setSelectedClientId(matchedClient.id);
        setSelectedClient(matchedClient);
      }
    }
  }, [client, clientList, clientDetails]);

  const handleScroll = (e) => {
    const container = e.target;
    const threshold = 5;
    const bottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <=
      threshold;

    const totalPages = Math.ceil(totalCount / 10);

    if (
      bottom &&
      !apiLoading &&
      clientList.length < totalCount &&
      page < totalPages
    ) {
      appInsights.trackEvent({
        name: "ClientSideBar- LoadMoreClients",
        properties: {
          page: page + 1,
          coachId,
        },
      });
      setPage((prevPage) => prevPage + 1);
    }
  };

  const handleClientSelect = (client) => {
    appInsights.trackEvent({
      name: "ClientSideBar- SelectClientFromSidebar",
      properties: {
        clientId: client?.id,
        coachId,
      },
    });

    setSelectedClientId(client?.id);
    setSelectedClient(client);
    setNotificationType(CONSTANTS.MEALS);

    client?.conversationId
      ? setIsChatPopupOpen(true)
      : setIsChatPopupOpen(false);
  };
  const handleReactive = async () => {
    appInsights.trackEvent({
      name: "ClientSideBar- ReactivateClient",
      properties: {
        clientId: selectedClientId,
        coachId,
      },
    });

    const resultAction = await dispatch(
      getClientInformation({ clientId: selectedClientId, coachId })
    );

    if (getClientInformation.fulfilled.match(resultAction)) {
      const clientData = resultAction.payload;

      try {
        const offerPayload = {
          leadId,
          firstName: clientData?.firstName,
          lastName: clientData?.lastName,
          email: clientData?.email,
          country: clientData?.country,
          phoneNumber: clientData?.phoneNumber,
          language: clientData?.language,
          note: clientData?.note,
          isLeadFromSite: clientData?.isLeadFromSite,
          createdOn: clientData?.createdOn,
          status: clientData?.status,
          isReactive: true,
        };

        navigate(`/leads/addNewOffer/${leadId}`, {
          state: offerPayload,
        });
      } catch (error) {
        console.error("Error sending offer:", error);
      }
    } else {
      console.error(
        "Failed to fetch client information:",
        resultAction.error.message
      );
    }
  };

  // Toast notification logic
  useEffect(() => {
    if (error) {
      // Trigger success toast
      setToastConfig({
        type: "error",
        message: error,
        heading: trans("Common.failed"),
      });
    }
  }, [error, trans]);

  return (
    <div className=" formContainer">
      <Row className="overflow-hidden">
        <Col xs={12} sm={4} md={3} lg={1}>
          <Button
            variant="outline-primary"
            size="sm"
            className="cancel-button d-inline-flex align-items-center"
            onClick={() => navigate("/clients")}
          >
            <i className="bi bi-chevron-left me-1"></i> {trans("Common.back")}
          </Button>

          <div
            ref={containerRef}
            className=" clientListContainer overflow-auto"
            style={{ height: `calc(100vh - ${160}px)`, overflowY: "auto" }}
            onScroll={handleScroll}
          >
            <List
              itemLayout="horizontal"
              dataSource={clientList}
              loading={loading}
              renderItem={(client) => (
                <List.Item
                  className={`clientListItem border-bottom-0 pb-2 ${
                    membershipOverviewLoading ? "disabled-item" : ""
                  }`}
                  onClick={() => {
                    if (membershipOverviewLoading) return;
                    handleClientSelect(client);
                  }}
                  id={client?.id}
                >
                  <div
                    style={{
                      borderLeft:
                        selectedClientId === client.id
                          ? "2px solid #1769bb"
                          : "2px solid #FFFFFF",
                    }}
                    className="client-avatar"
                  >
                    <Tooltip
                      title={
                        client?.firstName?.charAt(0)?.toUpperCase() +
                        client?.firstName?.slice(1)?.toLowerCase() +
                        " " +
                        client?.lastName?.charAt(0)?.toUpperCase() +
                        client?.lastName?.slice(1)?.toLowerCase()
                      }
                      overlayClassName="custom-tooltip"
                      placement="right"
                    >
                      <span>
                        <Avatar
                          src={client.profileURL}
                          size={60}
                          alt={
                            client.firstName?.charAt(0)?.toUpperCase() +
                            client.firstName?.slice(1)?.toLowerCase()
                          }
                        />
                      </span>
                    </Tooltip>
                    <span>
                      {client.firstName?.charAt(0)?.toUpperCase() +
                        client.firstName?.slice(1)?.toLowerCase()}
                    </span>
                  </div>
                </List.Item>
              )}
            />

            {loading && (
              <div className="text-align mt-2">
                <Spin />
              </div>
            )}
          </div>
        </Col>
        <Col xs={12} sm={8} md={9} lg={11}>
          <div className=" createClientHeading mt-2 p-0">
            <Row className="w-100 align-items-center">
              <Col xs={8}>
                <div className="d-flex align-items-start">
                  <span>
                    <Avatar
                      src={
                        (!membershipOverviewLoading &&
                          selectedClient?.profileURL) ||
                        "-"
                      }
                      // alt={firstName}
                      alt={
                        !membershipOverviewLoading &&
                        firstName?.charAt(0)?.toUpperCase() +
                          firstName?.slice(1)?.toLowerCase()
                      }
                      className="ml-3 mt-2 mr-2"
                      style={{ width: "60px", height: "60px" }}
                    />
                  </span>
                  <div>
                    <h5 className="ms-1 mt-2 d-inline">
                      {(!membershipOverviewLoading &&
                        firstName?.charAt(0)?.toUpperCase() +
                          firstName?.slice(1)?.toLowerCase()) ||
                        "-"}{" "}
                      {(!membershipOverviewLoading &&
                        lastName?.charAt(0)?.toUpperCase() +
                          lastName?.slice(1)?.toLowerCase()) ||
                        "-"}
                    </h5>
                    <hr className="m-0 w-100 text" />
                    <p className="ms-1 fs-6 font-normal mt-1 text-color-gray-40">
                      <Tooltip title="Age" arrow>
                        <span>
                          {(!membershipOverviewLoading &&
                            clientMeasurements?.age) ||
                            "-"}
                        </span>
                      </Tooltip>{" "}
                      |{" "}
                      <Tooltip title="Gender" arrow>
                        <span>
                          {!membershipOverviewLoading &&
                            (clientMeasurements?.sexAtBirth
                              ? clientMeasurements.sexAtBirth
                                  .charAt(0)
                                  .toUpperCase() +
                                clientMeasurements.sexAtBirth.slice(1)
                              : "-")}
                        </span>
                      </Tooltip>{" "}
                      |{" "}
                      <Tooltip title="Goal" arrow>
                        <span>
                          {!membershipOverviewLoading && goal
                            ? goal?.charAt(0).toUpperCase() + goal?.slice(1)
                            : "-"}
                        </span>
                      </Tooltip>
                    </p>
                  </div>
                </div>
              </Col>

              <Col xs={4} className="text-end align-items-center">
                {status === "Inactive" && (
                  <Tooltip
                    placement="right"
                    title={trans("ClientPaymentTab.sendReactivationOffer")}
                  >
                    <Button
                      className="saveButton"
                      onClick={() => handleReactive(client?.id)}
                    >
                      {"Reactivate"}
                    </Button>
                  </Tooltip>
                )}
              </Col>
            </Row>
            <div className="allign-reminder">
              {selectedClientId && (
                <Reminder selectedClientId={selectedClientId} />
              )}
              {(selectedClient?.conversationId || isChatPopupOpen) && (
                <ChatPopup
                  selectedClient={selectedClient}
                  setSelectedClient={setSelectedClient}
                />
              )}
              {/* <ChatbotContainer
                coachId={coachId}
                clientId={selectedClientId}
                status={status}
              /> */}
            </div>
          </div>

          <Row className="mt-3">
            <Col xs={6} sm={6} md={6} lg={6}>
              <h5 className="ms-4 mt-2 d-inline mb-2">
                {trans("Common.overview")}
              </h5>
              <div className="client-left-bar">
                <div className="overview_tab_border-bottom-custom" />
                <Row className=" createClientHeading p-3 ms-1 rounded-0 pb-0">
                  {selectedClientId && (
                    <MembershipOverview selectedClientId={selectedClientId} />
                  )}
                </Row>
                <Row className=" createClientHeading rounded-0  ms-1">
                  <hr />
                  <Row className="align-items-center">
                    <Col>
                      <h6>
                        {trans("Common.progress") +
                          " " +
                          trans("Common.overview")}
                      </h6>
                    </Col>
                    <Col className="text-end p-0">
                      <Button
                        className="saveButton"
                        onClick={() => setShowModal(true)}
                        disabled={
                          status === "Inactive" || status === "InActive"
                        }
                      >
                        <i className="bi bi-pencil"></i>{" "}
                        {trans("Client.editWeightGoal")}
                      </Button>
                    </Col>
                  </Row>
                  {showModal && (
                    <EditWeightGoalModal
                      show={showModal}
                      onClose={() => {
                        setShowModal(false);
                      }}
                      selectedClientId={selectedClientId}
                    />
                  )}
                  {selectedClientId && (
                    <AreaChartWithFilters selectedClientId={selectedClientId} />
                  )}
                </Row>
                <Row className=" createClientHeading p-3 ms-1 rounded-0 pb-0">
                  <hr />
                  <ClientCheckInsDetails
                    selectedClientId={selectedClientId}
                    status={
                      status === "Inactive" || status === "InActive"
                        ? true
                        : false
                    }
                  />
                </Row>
              </div>
            </Col>
            <Col xs={6} sm={6} md={6} lg={6}>
              {selectedClientId && (
                <ClientWorkoutTab
                  key={selectedClientId + notificationType}
                  selectedClientId={selectedClientId}
                  type={notificationType}
                  status={
                    status === "Inactive" || status === "InActive"
                      ? true
                      : false
                  }
                 clientDetails={{
                    ...clientDetails,
                    clientDetail: selectedClient || clientDetail,
                    isConversationEnabled: selectedClient?.conversationId 
                      ? true 
                      : (isConversationEnabled ?? false),
                  }}
                />
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      {toastConfig && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </div>
  );
};

export default ClientSideBar;
